import React from 'react';

export type ValuesType = {
  [key: string]: any;
};

export type ErrorsType = {
  [key: string]: string;
};

export type TouchedType = {
  [key: string]: boolean;
};

export type InputsMap = {
  [key: string]: {
    change: (value: any) => any;
    focus: () => any;
    blur: () => any;
  };
};

export type FormHookReturnValue = {
  inputs: InputsMap;

  submit: () => any;

  values: ValuesType;
  errors: ErrorsType;
  touched: TouchedType;
  isSubmitting: boolean;
};

export type FormHookParams = {
  initialValues?: ValuesType;
  validate: (values: ValuesType) => Promise<ErrorsType> | ErrorsType;
  submit: (values: ValuesType) => Promise<any> | any;
};

function formStateReducer(
  state: { values: ValuesType; changeCounter: number },
  action: { action: 'set' | 'update'; value: ValuesType }
) {
  switch (action.action) {
    case 'update':
      return { values: { ...state.values, ...action.value }, changeCounter: state.changeCounter + 1 };
    case 'set':
      return { values: action.value, changeCounter: state.changeCounter + 1 };
    default:
      return state;
  }
}

export default function useForm(params: FormHookParams): FormHookReturnValue {
  let { initialValues = {}, validate, submit } = params;

  let [isSubmitting, setIsSubmitting] = React.useState(false);

  let [errors, setErrors] = React.useState({});
  let [{ values, changeCounter }, dispatchValues] = React.useReducer(formStateReducer, {
    values: initialValues,
    changeCounter: 0,
  });

  let [touched, setTouched] = React.useReducer(
    (
      state: TouchedType,
      action: { action: 'blur' | 'focus' | 'change'; key: string } | { action: 'overwrite'; value: TouchedType }
    ) => {
      let stateClone = { ...state };

      switch (action.action) {
        case 'change':
        case 'blur':
          stateClone[action.key] = true;
          break;
        case 'focus':
          stateClone[action.key] = false;
          break;
        case 'overwrite':
          return action.value;
      }

      return stateClone;
    },
    {}
  );

  const runValidations = async () => {
    let errors = await validate(values);
    setErrors(errors);
    return errors;
  };

  const hasErrors = (errors: ErrorsType): boolean => {
    return !!Object.values(errors).find((v) => !!v);
  };

  const submitForm = async () => {
    setIsSubmitting(true);

    let touchedClone = { ...touched };
    for (let k of Object.keys(values)) {
      touchedClone[k] = true;
    }
    setTouched({ action: 'overwrite', value: { ...touched, ...touchedClone } });

    let errors = await runValidations();
    if (hasErrors(errors)) {
      setIsSubmitting(false);
      return;
    }

    await submit(values);

    setIsSubmitting(false);
  };

  React.useEffect(() => {
    runValidations();
  }, [changeCounter]);

  let inputs: InputsMap = React.useMemo(() => {
    let result = {};

    for (let key of Object.keys(initialValues)) {
      result[key] = {
        blur: () => {
          setTouched({
            action: 'blur',
            key,
          });
        },
        focus: () => {
          setTouched({
            action: 'focus',
            key,
          });
        },
        change: (val: any) => {
          setTouched({
            action: 'change',
            key,
          });

          dispatchValues({
            action: 'update',
            value: {
              [key]: val,
            },
          });
        },
      };
    }

    return result;
  }, [setTouched, dispatchValues, initialValues]);

  return {
    inputs,
    values,
    errors,
    touched,
    submit: submitForm,
    isSubmitting,
  };
}
